
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { Search, Plus, Image, FileAudio, FileVideo } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import MediaCard from '../media-library/MediaCard';
import { Badge } from "@/components/ui/badge";

interface MediaSelectorProps {
  type: "image" | "audio" | "video";
  language: string;
  onSelect: (file: any) => void;
}

export function MediaSelector({ type, language, onSelect }: MediaSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: mediaFiles, isLoading } = useQuery({
    queryKey: ['mediaSelector', type, language, search],
    queryFn: async () => {
      let query = supabase
        .from('media_files')
        .select('*')
        .eq('type', type);

      // Updated filter to include files with matching language OR null language_code
      if (language) {
        query = query.or(`language_code.eq.${language},language_code.is.null`);
      }

      // Add search filter if specified
      if (search) {
        query = query.ilike('filename', `%${search}%`);
      }

      // Order by creation date, newest first
      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error(`Error fetching ${type} files:`, error);
        throw error;
      }

      return data || [];
    },
    enabled: isOpen, // Only fetch when dialog is open
  });

  const handleSelect = (file: any) => {
    onSelect(file);
    setIsOpen(false);
  };

  // Get appropriate icon based on media type
  const getIcon = () => {
    switch (type) {
      case 'image': return <Image className="h-4 w-4 mr-2" />;
      case 'audio': return <FileAudio className="h-4 w-4 mr-2" />;
      case 'video': return <FileVideo className="h-4 w-4 mr-2" />;
      default: return <Plus className="h-4 w-4 mr-2" />;
    }
  };

  // Helper to render language indicator badge
  const renderLanguageBadge = (file: any) => {
    if (!file.language_code) {
      return <Badge variant="outline" className="absolute top-2 left-2 bg-black/50 text-white text-xs">Global</Badge>;
    }
    if (file.language_code === language) {
      return <Badge variant="default" className="absolute top-2 left-2 text-xs">{file.language_code}</Badge>;
    }
    return null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="mb-2">
          {getIcon()}
          Browse {type}s
        </Button>
      </DialogTrigger>
      <DialogContent className="w-full max-w-4xl max-h-[80vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl mb-4">
            Select {type.charAt(0).toUpperCase() + type.slice(1)}
          </DialogTitle>
        </DialogHeader>

        {/* Search bar */}
        <div className="relative mb-4">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={`Search ${type}s by filename...`}
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Media grid */}
        <div className="mt-4 bg-white rounded-lg p-4">
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {Array(8).fill(0).map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-md" />
              ))}
            </div>
          ) : mediaFiles && mediaFiles.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {mediaFiles.map((file) => (
                <div 
                  key={file.id} 
                  className="cursor-pointer hover:opacity-90 transition-opacity relative"
                  onClick={() => handleSelect(file)}
                >
                  {renderLanguageBadge(file)}
                  <MediaCard
                    file={file}
                    onClick={() => handleSelect(file)}
                    onDelete={() => {}}  // Empty function as deletion is not needed here
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                No {type}s found{search ? ` matching "${search}"` : ''}
              </p>
              <p className="text-sm mt-2">
                {language ? `Try uploading ${type}s in ${language} language or with no language assigned` : `Try uploading some ${type}s`}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
